import React from 'react'
import { FaArrowLeft, FaCheck, FaMinusCircle, FaPlus, FaRegCopy, FaShare, FaTimes } from 'react-icons/fa'
import { useHistory } from 'react-router'
import { useQuery, useMutation, useClient, useIsFetching } from 'jsonapi-react'
import classNames from 'classnames'
import find from 'lodash/find'
import map from 'lodash/map'
import maxBy from 'lodash/maxBy'
import parseInt from 'lodash/parseInt'
import sortBy from 'lodash/sortBy'
import filter from 'lodash/filter'
import get from 'lodash/get'

import { ActionButton, ActionButtonsGroup, Body } from 'layouts/basic'
import AlbumItem from 'components/Albums/Item'
import Button from 'components/Button'
import Input from 'components/Forms/Input'
import Textarea from 'components/Forms/Textarea'
import { Authenticated } from 'routes'
import useAuthStore from 'store/auth'
import Modal from 'components/Modal'
import Loader from 'components/Loader'
import Select from 'components/Select'
import Popover from 'components/Popover'
import Files from 'components/Files'
import DropUpload from 'components/DropUpload'


export default function ViewAlbumsScene(props) {
  const history = useHistory()
  const {id} = props.match.params
  const {isLogged} = useAuthStore()
  const client = useClient()
  const [searchUser, setSearchUser] = React.useState(null)
  const [showShareModal, setShowShareModal] = React.useState(false)
  const [isDeletingShareUser, setIsDeletingShareUser] = React.useState(false)
  const [showBulkModal, setShowBulkModal] = React.useState(false)
  const [isCreatingBulkItems, setIsCreatingBulkItems] = React.useState(false)

  const isFetching = useIsFetching()

  const { data:album={}, isLoading=true, refetch: refecthAlbum, error } = useQuery([
    'albums',
    id,
    {
      include: 'items.file,items.audio,created_by,shared_to_users.user',
    },
  ])
  const [updateAlbum] = useMutation([
    'albums',
    id,
  ])
  const [addAlbumItem, { isLoading: isAddingItem}] = useMutation([
    'albums',
    id,
    'items',
  ])

  const { data:users={}, isLoading: isLoadingUsers } = useQuery(searchUser && [
    'users',
    {
      'filter[search]': searchUser,
    },
  ])

  const [addShareAlbum, { isLoading: isSharingAlbum}] = useMutation([
    'albums',
    id,
    'share',
  ])

  const removeShare = async shareId => {
    setIsDeletingShareUser(true)
    try {
      await client.delete(['albums', id, 'share', shareId])
    } catch (error) {
      alert(error)
    }
    setIsDeletingShareUser(false)
  }

  let timeoutSearchUsers = null
  const inputSearchUser = e => {
    if (timeoutSearchUsers) {
      clearTimeout(timeoutSearchUsers)
    }
    timeoutSearchUsers = setTimeout(() => {
      setSearchUser(e.target.value)
    }, 800)
  }

  const itemUpdateField = (field, value, shareId) => {
    client.mutate([
      'albums',
      album.id,
      'share',
      shareId,
    ], {
      [field]: value,
    })
  }

  if (isLoading) {
    return (
      <Body>
        <Loader
          className='
          h-16 w-16
          rounded-b-full shadow-lg text-primary-600
          bg-white p-2
          fixed top-0 left-1/2 transform -translate-x-1/2
        '
          active={isFetching}
        />
      </Body>
    )
  }
  let timeouts = {
    description: null,
    title: null,
  }

  const deleteAlbum = async () => {
    await client.delete(['albums', album.id])
    history.goBack()
  }

  const onChangeField = (e, field) => {
    if (timeouts[field]) {
      clearTimeout(timeouts[field])
    }
    timeouts[field] = setTimeout(() => {
      updateAlbum({
        [field]: e.target.value
      })
    }, 800)
  }

  const createBulkItems = async selectedFiles => {
    const files = filter(
      selectedFiles,
      file => file.type === 'Video' || file.type === 'Image',
    )
    const audios = filter(selectedFiles, {type: 'Audio'})
    setIsCreatingBulkItems(true)
    await Promise.all(
      map(
        files,
        async (item, index) => {
          let linked = {
            file: {
              type: 'File',
              id: item.id,
            }
          }
          const audio = get(audios, `[${index}]`, false)
          if (audio) {
            linked = {
              ...linked,
              audio: {
                type: 'File',
                id: audio.id,
              }
            }
          }
          const order = maxBy(album.items, 'order') || {order: index}
          return await client.mutate(
            [
              'albums',
              album.id,
              'items',
            ],
            {
              order: order.order,
              ...linked
            }
          )
        }
      )
    )
    setIsCreatingBulkItems(false)
  }

  const hasPermission = album.permissions?.edit && isLogged

  return (
    <Authenticated redirect={!! error || album.privacy !== 'public'}>
      <Loader
        className='
          h-16 w-16
          rounded-b-full shadow-lg text-primary-600
          bg-white p-2
          z-50
          fixed top-0 left-1/2 transform -translate-x-1/2
        '
        active={isFetching}
      />
      <Modal active={showShareModal} onClose={() => setShowShareModal(false)}>
        <Modal.Content className='prose-sm lg:prose-lg'>
          <h2>Search users</h2>
          <Input
            onChange={inputSearchUser}
            inputProps={{
              placeholder: 'Email',
            }}
            loading={isLoadingUsers}
          />
          <div>
            {
              map(
                users,
                user => {
                  const isOwner = album.createdBy.id === user.id
                  const isUserAlreadySharedOrIsOwner = (
                    isOwner ||
                    find(album.sharedToUsers, { user: { id: user.id } })
                  )
                  return (
                    <div
                      key={`user_${user.id}`}
                      onClick={() => {
                        if ( ! isUserAlreadySharedOrIsOwner) {
                          addShareAlbum({
                            user: {
                              type: 'User',
                              id: user.id,
                            },
                            permission: 'view',
                          })
                        }
                      }}
                      className={classNames([
                        'p-2 transition-all flex items-center space-x-3',
                        {
                          'hover:bg-disabled-200 cursor-pointer': ! isUserAlreadySharedOrIsOwner,
                        }
                      ])}
                    >
                      {
                        isUserAlreadySharedOrIsOwner && (
                          <span className='m-0 text-primary-600'>
                            <FaCheck />
                          </span>
                        )
                      }
                      <Loader active={isSharingAlbum} />
                      <span>{user.fullName}</span>
                      {
                        isUserAlreadySharedOrIsOwner && (
                          isOwner ?
                          <b>album owner</b> :
                          <b>already shared</b>
                        )
                      }
                    </div>
                  )
                }
              )
            }
          </div>
          <div className='mt-16'>
            <h3>Shared to Users</h3>
            {
              map(
                album.sharedToUsers,
                sharedUser => {
                  return (
                    <div className='
                      flex space-x-2 items-strech justify-center
                      h-12
                      border-disabled-200 border-b last:border-b-0
                    '>
                      <div className='flex-grow flex items-center pl-3'>{sharedUser.user.fullName}</div>
                      <div className='flex items-center'>
                        <Select
                          selectProps={{
                            defaultValue: sharedUser.permission,
                            name: 'privacy',
                          }}
                          onChange={e => itemUpdateField('permission', e.target.value, sharedUser.id)}
                          options={[
                            {
                              value: 'view',
                              label: 'View',
                            },
                            {
                              value: 'edit',
                              label: 'Edit',
                            },
                            {
                              value: 'manage',
                              label: 'Manage',
                            },
                          ]}
                        />
                      </div>
                      <Button
                        loading={isDeletingShareUser}
                        theme='danger'
                        onClick={() => removeShare(sharedUser.id)}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  )
                }
              )
            }
          </div>
        </Modal.Content>
      </Modal>
      <Modal
        active={showBulkModal}
        fullscreen
        onClose={() => {
          refecthAlbum()
          setShowBulkModal(false)
        }}
      >
        <Modal.Content>
          <Files
            title='Select files from library'
            labelSubmit='Use in album'
            onSubmit={ async selectedFiles => {
              try {
                await createBulkItems(selectedFiles)
              } catch(error) {
                alert('Error in bulk upload. Some files are not linked.')
              }
              setShowBulkModal(false)
            }}
          />
        </Modal.Content>
      </Modal>
      <Body loading={isCreatingBulkItems}>
        <DropUpload active={album.permissions?.addItems} uploadedFiles={createBulkItems}>
          <div className='prose-lg mb-20'>
            {
              hasPermission ?
              <Input
                onChange={e => onChangeField(e, 'title')}
                inputProps={{
                  placeholder: "Album's title",
                  defaultValue: album.title,
                }}
              /> :
              <h1>{album.title}</h1>
            }
            {
              hasPermission ?
              <Textarea
                inputProps={{
                  placeholder: 'Description',
                  defaultValue: album.description,
                }}
                onChange={e => onChangeField(e, 'description')}
              /> :
              <p>{album.description}</p>
            }
            {
              album?.createdBy &&
              <p>by {album.createdBy.fullName}</p>
            }
          </div>
          <div className='
            sticky top-0
            bg-white
            z-20 h-12 sm:h-16
            flex items-center transform
            shadow-lg
          '>
            <div className='flex items-center flex-grow h-full'>
              <Button className='h-full px-4 sm:px-6' onClick={() => history.goBack()}>
                <FaArrowLeft className='' />
              </Button>
              Album: {album.title}
            </div>
            {
              album.permissions?.edit &&
              <>
                <div className='flex h-full items-center justify-end'>
                  <Select
                    selectProps={{
                      defaultValue: album.privacy,
                      name: 'privacy',
                    }}
                    className='h-full'
                    onChange={e => updateAlbum({'privacy': e.target.value})}
                    options={[
                      {
                        label: 'Public',
                        value: 'public',
                      },
                      {
                        label: 'Private',
                        value: 'private',
                      },
                    ]}
                  />
                </div>
                <div className='flex items-center justify-end h-full'>
                  <Button
                    className='h-full px-4 sm:px-6'
                    theme={album.shared ? 'success' : 'default'}
                    onClick={() => setShowShareModal(true)}
                  >
                    <FaShare />
                  </Button>
                  {/* <Button className='h-full' theme={album.favorite ? 'success' : 'default'} onClick={() => history.goBack()}>
                    <FaStar />
                  </Button> */}
                </div>
              </>
            }
          </div>
          <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 mb-32 gap-1'>
            {
              map(
                sortBy(album.items, item => parseInt(item.order, 10)),
                item => (
                  <AlbumItem
                    hasPermission={hasPermission}
                    key={`album_item_${item.id}`}
                    {...item}
                  />
                )
              )
            }
          </div>
          {
            hasPermission &&
            <Popover
              position='left'
              panelClassName='ring-1 ring-danger-600'
              handler={
                <Button
                  theme='danger'
                  childrenContainerClassName='group flex items-center vertical-middle space-x-3'
                >
                  <FaMinusCircle className='w-8 h-8 bg-white rounded-full' />
                  <span>Delete album</span>
                </Button>
              }
            >
              This action is not reversible.
              <Button
                onClick={deleteAlbum}
                theme='danger'
                className='w-full inverted mt-3'
              >Delete</Button>
            </Popover>
          }
          {
            album.permissions?.addItems &&
            <ActionButtonsGroup>
              <ActionButton
                onClick={() => addAlbumItem({
                  album_id: album.id,
                  order: get(maxBy(album.items, 'order'), 'order', 0),
                })}
              >
                {
                  isAddingItem ?
                  <Loader active className='text-white' /> :
                  <FaPlus />
                }
              </ActionButton>
              <ActionButton
                title='Bulk Upload'
                onClick={() => setShowBulkModal(true)}
              >
                <FaRegCopy />
              </ActionButton>
            </ActionButtonsGroup>
          }
        </DropUpload>
      </Body>
    </Authenticated>
  )
}
